import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import {
  getMenuCardStyle,
  getMenuMutedTextStyle,
  getMenuPrimaryButtonStyle,
} from '@/lib/menuTheme';

export function CatalogResolvingBusiness() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background px-6">
      <Loader2 className="w-12 h-12 animate-spin text-primary" />
      <p className="text-sm text-muted-foreground text-center">{t('catalog.loadingMenu')}</p>
    </div>
  );
}

export function CatalogBusinessNotFound() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="p-10 text-center max-w-md space-y-3">
        <h2 className="text-xl font-semibold">{t('catalog.businessNotFoundTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('catalog.businessNotFoundBody')}</p>
      </Card>
    </div>
  );
}

export function CatalogLoadingScreen({ standard, pageStyle, premium, accent }) {
  return (
    <div
      className={cn('min-h-screen flex items-center justify-center', standard && 'bg-background')}
      style={pageStyle}
    >
      <Loader2
        className={cn('w-12 h-12 animate-spin', premium ? '' : 'text-primary')}
        style={premium ? { color: accent } : undefined}
      />
    </div>
  );
}

export function CatalogErrorScreen({
  routeBid,
  loadError,
  standard,
  pageStyle,
  premium,
  business,
  branding,
  onRetry,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div
      className={cn('min-h-screen flex items-center justify-center p-6', standard && 'bg-background')}
      style={pageStyle}
    >
      <Card
        className="p-12 text-center max-w-md space-y-4"
        style={premium ? getMenuCardStyle(business) : undefined}
      >
        {loadError ? (
          <>
            <h2
              className="text-2xl font-bold"
              style={premium ? { color: branding.text_color } : undefined}
            >
              {t('catalog.loadErrorTitle')}
            </h2>
            <p
              className={cn('text-sm', !premium && 'text-muted-foreground')}
              style={premium ? getMenuMutedTextStyle(business) : undefined}
            >
              {t('catalog.loadErrorBody')}
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center flex-wrap">
              <Button
                type="button"
                style={premium ? getMenuPrimaryButtonStyle(business) : undefined}
                onClick={onRetry}
              >
                {t('common.retry')}
              </Button>
              <Button
                type="button"
                variant="outline"
                style={
                  premium
                    ? {
                        borderColor: branding.card_border_color,
                        color: branding.text_color,
                        backgroundColor: branding.card_background_color,
                      }
                    : undefined
                }
                onClick={() => window.location.reload()}
              >
                {t('common.reloadPage')}
              </Button>
              <Button
                variant="outline"
                style={
                  premium
                    ? {
                        borderColor: branding.card_border_color,
                        color: branding.text_color,
                        backgroundColor: branding.card_background_color,
                      }
                    : undefined
                }
                onClick={() => navigate(getPublicMenuPath(routeBid, 'home'))}
              >
                {t('catalog.backHome')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2
              className="text-2xl font-bold"
              style={premium ? { color: branding.text_color } : undefined}
            >
              {t('catalog.menuUnavailable')}
            </h2>
            <Button
              variant="outline"
              style={
                premium
                  ? {
                      borderColor: branding.card_border_color,
                      color: branding.text_color,
                      backgroundColor: branding.card_background_color,
                    }
                  : undefined
              }
              onClick={() => navigate(getPublicMenuPath(routeBid, 'home'))}
            >
              {t('catalog.backHome')}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
